import type { ShowToastOptions } from '../contexts/ToastContext';

export const UNDO_WINDOW_MS = 5000;

export type UndoableNotify = (
  message: string,
  options?: ShowToastOptions,
) => void;

export type ScheduleUndoableActionOptions = {
  /** Dedupes pending windows; same key flushes the prior commit first. */
  key: string;
  message: string;
  apply: () => void;
  revert: () => void;
  commit: () => void | Promise<void>;
  windowMs?: number;
  notify: UndoableNotify;
  /** Error toast when commit fails (after revert). */
  commitErrorMessage?: string;
};

type Pending = {
  key: string;
  timer: ReturnType<typeof setTimeout>;
  revert: () => void;
  commit: () => void | Promise<void>;
  undone: boolean;
  flushed: boolean;
};

const pendingByKey = new Map<string, Pending>();
let pagehideBound = false;

function ensurePagehideFlush(): void {
  if (pagehideBound || typeof window === 'undefined') return;
  pagehideBound = true;
  window.addEventListener('pagehide', () => {
    void flushAllPendingCommits();
  });
}

async function runCommit(entry: Pending): Promise<void> {
  if (entry.undone || entry.flushed) return;
  entry.flushed = true;
  clearTimeout(entry.timer);
  if (pendingByKey.get(entry.key) === entry) pendingByKey.delete(entry.key);
  try {
    await entry.commit();
  } catch {
    entry.revert();
    throw new Error('UNDOABLE_COMMIT_FAILED');
  }
}

/** Test / teardown helper — clears pending timers without committing. */
export function resetUndoableActionsForTests(): void {
  for (const entry of pendingByKey.values()) {
    clearTimeout(entry.timer);
  }
  pendingByKey.clear();
}

export function pendingUndoableActionCountForTests(): number {
  return pendingByKey.size;
}

export async function flushAllPendingCommits(): Promise<void> {
  const entries = [...pendingByKey.values()];
  pendingByKey.clear();
  for (const entry of entries) {
    if (entry.undone || entry.flushed) continue;
    entry.flushed = true;
    clearTimeout(entry.timer);
    try {
      await entry.commit();
    } catch {
      entry.revert();
    }
  }
}

/**
 * Optimistic apply + delayed commit. Undo cancels the pending write (no reverse API).
 * Closing the toast with X does not undo — commit still fires at window end.
 */
export function scheduleUndoableAction(opts: ScheduleUndoableActionOptions): void {
  ensurePagehideFlush();
  const windowMs = opts.windowMs ?? UNDO_WINDOW_MS;
  const prior = pendingByKey.get(opts.key);
  if (prior && !prior.undone && !prior.flushed) {
    // Flush previous commit synchronously (fire-and-forget await chain).
    void runCommit(prior).catch(() => {
      /* revert already applied in runCommit */
    });
  }

  opts.apply();

  const entry: Pending = {
    key: opts.key,
    revert: opts.revert,
    commit: opts.commit,
    undone: false,
    flushed: false,
    timer: setTimeout(() => {
      void runCommit(entry)
        .catch(() => {
          opts.notify(opts.commitErrorMessage ?? 'La suppression a échoué.', {
            tone: 'error',
          });
        });
    }, windowMs),
  };
  pendingByKey.set(opts.key, entry);

  opts.notify(opts.message, {
    tone: 'info',
    autoDismissMs: windowMs,
    action: {
      label: 'Annuler',
      onClick: () => {
        if (entry.undone || entry.flushed) return;
        entry.undone = true;
        clearTimeout(entry.timer);
        if (pendingByKey.get(opts.key) === entry) pendingByKey.delete(opts.key);
        opts.revert();
      },
    },
  });
}
